
import {first} from 'rxjs/operators';
import {forkJoin} from 'rxjs';

import {ProjectElement} from './project.element';
import {ProjectElementType} from './project.element.type';
import {Project} from '../project';

import {BlobType} from '../blob';

import {Logger} from '@/util/logger';

import {KPIElement} from '@/models/project/k.p.i.element';
import {ProjectElementFactory} from '@/models/project.element.factory';

import {basename, dirname, empty, errorAsText} from '@/util/util';
import {StringLineViewerMetadata} from '@/screens/ide/string.line.viewer.metadata';
import {JSONBrowserMetadata} from '@/screens/ide/json.browser.metadata';
import {ProjectElementFactoryDecorator} from '@/models/project/project.element.factory.decorator';
import {PropertiesHolder} from '@/models/properties.holder';
import {FileBrowserMetadata} from '@/screens/ide/file.browser.metadata';
import {ConnectionViewerMetadata} from '@/screens/ide/connection.viewer.metadata';


/**
 *
 * @class LineSimElement
 *
 * node to maps to a simulation scenario and train movement (input and output)
 * It's not a "job" is a calculated result we get from historical data via locoquery.
 * (i.e. BigQuery)
 *
 * When given a blob key path for a scenario and a movement...this node takes
 * ownership.  Deleting this node, will also delete those two blobs.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      February 2022
 *
 */
@ProjectElementFactoryDecorator('LineSimElement')
export class LineSimElement extends ProjectElement {

  protected actionKeys: string[] = [
    'viewstringline',
    'downloadinput',
    'downloadoutput',
    'viewinput',
    'viewoutput',
    'renameresult',
    'deleteresult',
    'generatekpi',
    'viewinbrowser',
    'connectionviewer'
  ];

  /** blob key for the scenario (plant + traffic) */
  public _scenario: string = '';

  /** blob key for the train movement */
  public _movement: string = '';

  /** start date bracket */
  public _start: string = '';

  /** end date bracket */
  public _end: string = '';

  /** the corridor */
  public _corridor: string = '';

  /** standard constructor */
  constructor(
    project:     Project,
    id:          string,
    scenarioKey: string,
    movementKey: string,
    start:       string,
    end:         string,
    corridor:    string,
    parent:      ProjectElement,
    publish?:    boolean,
    peType?:     ProjectElementType
  ) {

    super(project, id, peType ? peType : ProjectElementType.LineSimResultCalculated);

    this._scenario    = scenarioKey;
    this._movement    = movementKey;
    this._start       = start;
    this._end         = end;
    this._corridor    = corridor;

    this._label       = 'Stringline ' + start + '_' + end;
    this._icon        = 'fas fa-train';
    this._elementKind = 'LineSimResultCalculated';

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring LineSimElement");
  }

  /** drop data on this element */
  public acceptDrop(data): boolean {

    return false;
  }

  /** is this element deletable? */
  public isDeletable(): boolean {

    const kids: ProjectElement[] = this.children();

    return kids.length <= 0;
  }

  /** test if any blob of this type can be a child of this element */
  public acceptsBlobType(type: BlobType): boolean {

    return false;
  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    return super.node({
      scenario: this._scenario,
      movement: this._movement,
      start:    this._start,
      end:      this._end,
      corridor: this._corridor
    });
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any) {

    /* recover the basics */

    super.fromNode(node);

    /* recover the job details */

    this._scenario = node.scenario;
    this._movement = node.movement;
    this._start    = node.start;
    this._end      = node.end;
    this._corridor = node.corridor;
  }

  /*
   * when blobs get moved/copied in bulk, project elements have to allow being told where
   * their blobs have been moved to.
   *
   */
  public adjusted(searchKey: string, replaceKey: string) {

    if (this._scenario === searchKey) {

      this._scenario = replaceKey;
    }

    if (this._movement === searchKey) {

      this._movement = replaceKey;
    }

    super.adjusted(searchKey, replaceKey);
  }

  /** fetch any secondary blobs for this element */
  public secondaryBlobs(): string[] {

    return [this._scenario, this._movement];
  }

  /** test if the output has been downloaded yet */
  public hasInput(): boolean {

    return !empty(this._scenario);
  }

  /** test if the output has been downloaded yet */
  public hasOutput(): boolean {

    return !empty(this._movement);
  }

  /** test if the log has been downloaded yet */
  public hasLog(): boolean {

    return false;
  }

  /**
   * subclasses must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    actionSet.viewstringline = {
      name: 'String Line View',
      icon: 'fas fa-train',
      disabled: !this.hasOutput()
    };

    actionSet.connectionviewer = {
      name: 'Connection Viewer',
      icon: 'fas fa-project-diagram',
      disabled: !this.hasOutput()
    };

    actionSet.downloadinput = {
      name: 'Scenario (Download)',
      icon: 'fas fa-file-download',
      disabled: !this.hasInput()
    };

    actionSet.downloadoutput = {
      name: 'Movement (Download)',
      icon: 'fas fa-file-download',
      disabled: !this.hasOutput()
    };

    actionSet.viewinput = {
      name: 'Scenario View',
      icon: 'fas fa-keyboard',
      disabled: !this.hasOutput()
    };

    actionSet.viewoutput = {
      name: 'Movement View',
      icon: 'fas fa-keyboard',
      disabled: !this.hasOutput()
    };

    actionSet.generatekpi = {
      name: 'Generate KPI',
      icon: 'far fa-chart-bar',
      disabled: !this.hasOutput() || (this.children().length > 0)
    };

    actionSet.viewinbrowser = {
      name: 'Folder Of',
      icon: 'fas fa-list',
      disabled: false
    };

    actionSet.renameresult = {
      name: 'Rename Result',
      icon: 'fas fa-tag',
      disabled: false
    };

    actionSet.deleteresult = {
      name: 'Delete Result',
      icon: 'fas fa-trash',
      disabled:  false
    };

    return actionSet;
  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!this.actionKeys.find(x => x === key);
  }

  /** append these elements properties to the holder */
  public properties(properties?: PropertiesHolder): PropertiesHolder {

    if (!properties) {

      properties = new PropertiesHolder();

    }

    properties.set('Start',    this._start);
    properties.set('End',      this._end);
    properties.set('Corridor', this._corridor);
    properties.set('Input',    this._scenario);
    properties.set('Output',   this._movement);

    /* add in the usual properties */

    return super.properties(properties);
  }

  /** generate the KPI child for this line sim job */
  public generateKPI(): void {

    const lse:    LineSimElement = this;
    const      kpiFolder: string = dirname(this._movement);
    const         kpiKey: string = kpiFolder + lse._parent.basename() + '.kpi';

    Logger.debug('[result] generating  KPI:', lse._scenario, lse._movement, kpiKey);

    /*
     * we create the KPI element immediately, even though it isn't usable...so that we can give
     * the user some visual feedback that it is pending...
     *
     */

    const child: KPIElement = ProjectElementFactory.fromObject(
      ProjectElementType.KPI, {
        project: lse._project,
        id:      '',
        input:   '',
        output:  '',
        kpi:     '',
        kind:    ProjectElementType.KPI,
        parent:  lse,
        publish: true
      }) as KPIElement;

    child.setStatus('Pending...', 'pending');

    /* make sure the thing we just created is visible */

    lse._project.expand([lse]);

    lse.save();

    /* generate the KPI and save it to our chose KPI blob location... */

    lse._project.services.kpi.kpi(lse._scenario, lse._movement, kpiKey)
      .pipe(first())
      .subscribe({

        next: (triplet: any): void => {

          Logger.debug(`[result] saving KPI:`, triplet);

          /* the KPI generation succeeded, save the co-ordinates ... */

          child._inputKey  = triplet.input;
          child._outputKey = triplet.output;
          child._kpiKey    = triplet.kpi;

          /* update the display to success */

          child.setStatus('', 'complete');

          /* trigger a project save */

          lse.save();
        },

        error: (err: any): void => {

          /* update the display to failed */

          let errMsg: string = errorAsText(err);

          child.setStatus(errMsg, 'failed');

          Logger.error(`[result] can't generate kpi blob: `, err);

          /* trigger a project save */

          lse.save();
        }

      });

  }

  /** helper to delete any cached artifacts from the remote simulation */
  public deleteResult(selection: ProjectElement[]) {

    if (this.pendingKeys.length > 0) {

      /* busy right now */

      return ;
    }

    /*
     * the cached results are internal to a "result", we just need to batch them up,
     * and remove them all with a fork/join.  In our case there are just two artifacts,
     * the log and the output.
     *
     */

    this.pendingKeys = [];

    this.pendingKeys.push([this._scenario, 'latest']);
    this.pendingKeys.push([this._movement, 'latest']);

    /* build out the blob deletion observables ... */

    Logger.debug('[result] deleting blobs: ', this.pendingKeys);

    /* create the observables for copying blobs... */

    const observables: any[] = [];

    for (const f of this.pendingKeys) {

      /* as we copy we are also forcing the type to be whatever the user selected as the import type */

      observables.push( this._project.services.blobber.remove(f[0], f[1]).pipe(first()) );
    }

    /* now fork & join to do them all in parallel */

    forkJoin(observables).subscribe(
      (results) => {

        /* everything got deleted ok, add the children in the project and save the latest project */

        Logger.debug('[result] blobs deleted: ', results);

        /* make sure the project is saved */

        this.pendingKeys = [];

        /* delete self */

        this.deleteElements([this]);

      },
      (error) => {

        Logger.error('[result] problems deleting blobs: ', error);

      });

  }

  /** subclasses must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    if ( !this.actionKeys.find(x => x === key) ) {

      /* let ProjectElement handle it */

      this._parent.action(key, selection);

      return ;
    }

    switch (key) {

      case 'viewinbrowser': {

        this._project.services.ide.launchWindow(FileBrowserMetadata.build(this._scenario, this._scenario));

        break;
      }

      case 'connectionviewer': {

        this._project.services.ide.launchWindow(new ConnectionViewerMetadata('', '', this._scenario));

        break;
      }

      case 'generatekpi': {

        this.generateKPI();
      }
      break;

      case 'viewstringline': {

        this._project.services.ide.launchWindow(
          new StringLineViewerMetadata(
            '',
            '',
            this._scenario,
            this._movement
          )
        );

        break;
      }

      case 'downloadinput': {

        this.dowloadBlob(BlobType.Json, this._scenario, 'latest');
        break;
      }

      case 'downloadoutput': {

        this.dowloadBlob(BlobType.Json, this._movement, 'latest');
        break;
      }

      case 'viewinput': {

        this._project.services.ide.launchWindow(
          new JSONBrowserMetadata(
            '',
            '',
            this._scenario
          )
        );

        break;
      }

      case 'viewoutput': {

        this._project.services.ide.launchWindow(
          new JSONBrowserMetadata(
            '',
            '',
            this._movement
          )
        );

        break;
      }

      case 'renameresult': {

        const lsr: LineSimElement = this;

        lsr._project.services.modals.open(
          'rename-element-modal',
          (name, resolve, reject) => {

            name = name.trim();

            Logger.debug('[LineSimElement] validating: ', name);

            /* make sure we aren't name clashing ...  */

            const peers: ProjectElement[] = lsr._parent._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('Result of that name already exists.');

                return ;
              }

            }

            resolve(name);

          },
          (name) => {

            /* new label was accepted */

            Logger.debug('[LineSimElement] renaming: ', name);

            lsr._label = `${name.trim()}`;

            lsr.redraw();

            lsr.save();

          },
          {
            title: 'Rename Result',
            placeholder: this._label
          });

      }
        break;

      case 'deleteresult': {

        Logger.debug('[LineSimElement] deleting');

        this.deleteElements(selection);

        break;
      }

    }
  }


}
